import { Component, OnInit, ViewChild, ElementRef, AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import * as fabric from 'fabric';
import { Dialog } from '../../../core/providers/modal/modal-types';
import { NGXLogger } from 'ngx-logger';
import { notNullOrUndefined } from '../../../common/utils/not-null-or-undefined';
import {
    ProductCustomizationPrintTemplate,
    ProductCustomizationTextFramesData,
    ProductCustomizationOption,
    FrameState,
    ProductCustomizationPaidOption,
    ProductCustomizationMessageOption,
    PaidOptionItem,
    TextFrameAssetConfig,
    TextFrameGroupState,
    TextFrameGroupConfig,
    TextFrameColorOption,
    normalizeTextFrameColor,
} from '../../../common/interfaces';
import { FontLoaderService } from '../../../core/providers/font-loader/font-loader.service';
import { ActiveService } from '../../../core/providers/active/active.service';
import { CustomizableAssetInput, Order } from '../../../common/generated-types';
import { CustomImageUploadService } from '../../../core/providers/custom-image-uploader/custom-image-uploader.service';
import { NotificationService } from '../../../core/providers/notification/notification.service';
import { DataService } from '../../../core/providers/data/data.service';
import { forkJoin } from 'rxjs';
import { finalize, take } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';
import { GET_PRINT_FONT_CATALOG } from '../../../common/graphql/documents.graphql';

const DEFAULT_LABEL_FONT_SIZE = 16;
const DEFAULT_LABEL_COLOR = '#000000';

@Component({
    selector: 'vsf-image-customizer-modal',
    styleUrls: ['./image-customizer-modal.component.scss'],
    templateUrl: './image-customizer-modal.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageCustomizerModalComponent implements OnInit, AfterViewInit, OnDestroy, Dialog<CustomizableAssetInput[]> {

    // --- Dialog Interface ---
    resolveWith: (result?: CustomizableAssetInput[]) => void;
    printingPattern: ProductCustomizationPrintTemplate | null;
    messageOptions: ProductCustomizationMessageOption[] = [];
    paidOptions: ProductCustomizationPaidOption[] = [];
    textFrameOptions: { optionId: string; data: ProductCustomizationTextFramesData }[] = [];
    customizationOptions: ProductCustomizationOption[] = [];
    // Interleaved render/persist order of the non-print customization options,
    // mirroring the configured `customizationOptions` sequence. `textframe`
    // entries carry the source option id so the matching standalone overlay
    // groups render (and submit) inline at their configured position rather
    // than always trailing. `index` is unused for `textframe`.
    orderedOptions: Array<{kind: 'message' | 'paid' | 'textframe', index: number, optionId?: string}> = [];
    productVariantId: string;
    variantName: string;
    variantPrice: number;
    title: string;
    description: string;
    isMobile: boolean = false;

    // --- Canvas Elements ---
    @ViewChild('canvasContainer') canvasContainer: ElementRef<HTMLDivElement>;
    @ViewChild('editorCanvas') editorCanvasEl: ElementRef<HTMLCanvasElement>;
    /** Outer scroll container — nudged by nudgeRepaint() to force a repaint. */
    @ViewChild('modalScroll') modalScroll: ElementRef<HTMLDivElement>;
    private editorCanvas: fabric.Canvas;
    private backgroundImage: fabric.Image | null = null;
    private destroyed = false;

    // --- Component State ---
    frameStates: FrameState[] = [];
    backgroundImageUrl: string | null = null;
    isCanvasReady = false;
    isUploadingBackground = false;
    isProcessingSubmit = false;
    isImageProcessing = false;
    validationWarnings: { [key: number]: boolean } = {};
    missingImagesCount = 0;
    uploadMergedCanvasFailed = false;
    predefinedSelections: (string | null)[] = [];
    predefinedValidation:  { [key: number]: boolean } = {};
    paidSelections: (PaidOptionItem | null)[] = [];
    paidValidation: Record<number, boolean> = {};
    messageInputs: string[] = [];
    messageValidation: { [idx: number]: boolean } = {};
    messagesRequired = false;
    readonly maxNotesLength = 600;
    readonly maxLabelWindowRatio = 0.8;
    readonly transparency = 0.2;
    readonly maxImageSizeMB = 20;
    notesExpanded = false;
    activeOrder: Order | null = null;
    selectedBackgroundIndex = 0;
    buttonText: string = 'Add to Cart';
    textGroupStates: TextFrameGroupState[] = [];
    isFontLoading = false;

    /**
     * Nested text-overlay groups (bound to a specific image frame) paired with
     * their original index in `textGroupStates`. Rendered directly under the
     * image-uploader section so each frame's overlays sit next to its upload.
     * Built once in initTextFrameGroups() — must NOT be a getter: returning
     * fresh wrapper objects each call makes *ngFor recreate every row on every
     * change-detection pass, which freezes the modal under OnPush.
     */
    nestedTextGroupEntries: { gs: TextFrameGroupState; gi: number }[] = [];

    /**
     * Standalone (free-floating) text-overlay groups paired with their original
     * index. Rendered after the ordered customization options. Built once — see
     * the note on nestedTextGroupEntries about stable identity.
     */
    standaloneTextGroupEntries: { gs: TextFrameGroupState; gi: number }[] = [];

    /**
     * Standalone overlay entries grouped by their source `custom-text-frames`
     * option id (group order preserved within each option). The template indexes
     * this map by `orderedOptions` `textframe` entries so each option's overlays
     * render inline at its configured position. Built once for stable *ngFor
     * identity under OnPush (same rationale as the entries arrays above).
     */
    standaloneEntriesByOption: Record<string, { gs: TextFrameGroupState; gi: number }[]> = {};

    /**
     * Standalone overlay entries whose source option id is NOT present in
     * `orderedOptions` (defensive: the legacy no-customizationOptions path).
     * Rendered after the ordered options so nothing is silently dropped.
     */
    unorderedStandaloneEntries: { gs: TextFrameGroupState; gi: number }[] = [];

    /**
     * Flattened, fully-ordered render list for the non-print options area
     * (message / paid / standalone overlay groups), in configured settings
     * order, followed by any unmatched standalone overlays. The template
     * iterates this ONCE — no nested structural directives or per-pass context
     * literals — so interacting with one option can't disturb the rendering of
     * the others. Built once in rebuildTextGroupEntries() for stable identity
     * under OnPush. Overlay items carry their stable {gs, gi} so they double as
     * the *ngTemplateOutlet context (no fresh object per change-detection pass).
     */
    renderItems: Array<
        | { kind: 'message'; index: number }
        | { kind: 'paid'; index: number }
        | {
              kind: 'overlay';
              gs: TextFrameGroupState;
              gi: number;
              showSubheading: boolean;
              subheadingLabel?: string;
              subheadingDescription?: string;
          }
    > = [];

    /** trackBy for the overlay *ngFor rows — keyed on the stable original index. */
    trackTextGroupEntry(_i: number, entry: { gi: number }): number {
        return entry.gi;
    }

    /**
     * Split textGroupStates into nested (frame-bound) vs standalone entries,
     * each carrying its original index. Also bucket the standalone entries by
     * source option id (for inline, config-ordered rendering) and compute the
     * leftover set whose option isn't represented in orderedOptions. Called once
     * after the groups are built (orderedOptions must already be populated).
     */
    private rebuildTextGroupEntries(): void {
        const withIndex = this.textGroupStates.map((gs, gi) => ({ gs, gi }));
        this.nestedTextGroupEntries = withIndex.filter(e => !!e.gs.parentImageFrameName);
        this.standaloneTextGroupEntries = withIndex.filter(e => !e.gs.parentImageFrameName);

        // Bucket standalone entries by source option id (insertion order kept)
        // so the template can render each option's overlays inline at its
        // configured position among the other customization options.
        this.standaloneEntriesByOption = {};
        for (const entry of this.standaloneTextGroupEntries) {
            const key = entry.gs.sourceOptionId || '';
            (this.standaloneEntriesByOption[key] ??= []).push(entry);
        }
        // Standalone entries whose option id isn't an orderedOptions `textframe`
        // entry fall back to the trailing block (keeps the legacy
        // no-customizationOptions path and any unmatched group visible).
        const orderedOptionIds = new Set(
            this.orderedOptions
                .filter(o => o.kind === 'textframe')
                .map(o => o.optionId || ''),
        );
        this.unorderedStandaloneEntries = this.standaloneTextGroupEntries.filter(
            e => !orderedOptionIds.has(e.gs.sourceOptionId || ''),
        );

        this.buildRenderItems();
    }

    /**
     * Flatten orderedOptions (+ the unordered standalone fallback) into a single
     * ordered render list. Standalone overlay options expand into one item per
     * overlay group, with a subheading flag on the first group of each option.
     * The template iterates this list once — see the `renderItems` field note.
     */
    private buildRenderItems(): void {
        this.renderItems = [];
        for (const entry of this.orderedOptions) {
            if (entry.kind === 'message') {
                this.renderItems.push({ kind: 'message', index: entry.index });
            } else if (entry.kind === 'paid') {
                this.renderItems.push({ kind: 'paid', index: entry.index });
            } else if (entry.kind === 'textframe') {
                this.appendOverlayItems(this.standaloneEntriesByOption[entry.optionId || ''] || []);
            }
        }
        // Unmatched standalone overlays (legacy no-customizationOptions path)
        // render after the configured options so nothing is dropped.
        this.appendOverlayItems(this.unorderedStandaloneEntries);
    }

    /**
     * Append overlay render items for the given standalone entries, flagging a
     * subheading on the first group of each distinct source option (so each
     * option gets exactly one label/description divider).
     */
    private appendOverlayItems(entries: { gs: TextFrameGroupState; gi: number }[]): void {
        let prevOptionId: string | null = null;
        let first = true;
        for (const e of entries) {
            const optionId = e.gs.sourceOptionId || '';
            const showSubheading = first || optionId !== prevOptionId;
            this.renderItems.push({
                kind: 'overlay',
                gs: e.gs,
                gi: e.gi,
                showSubheading,
                subheadingLabel: showSubheading ? (e.gs.sourceOptionLabel || 'Text Overlays') : undefined,
                subheadingDescription: showSubheading ? e.gs.sourceOptionDescription : undefined,
            });
            prevOptionId = optionId;
            first = false;
        }
    }
    private serverFontCatalog: { family: string; category: string; googleFontsUrl: string }[] | null = null;
    private originalW = 0;
    private originalH = 0;
    private canvasScale = 1;
    private readonly allowedMimeTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    private isBrowser: boolean = false;
    private heic2anyPromise: Promise<any> | null = null;
    private lastMtrClickTime = 0;
    private lastMtrClickTarget: fabric.FabricObject | null = null;

    private readonly fabricDefaults = {
        transparentCorners: false,
        cornerColor: 'blue',
        cornerStyle: 'circle',
        borderColor: 'blue',
        borderDashArray: [3, 3],
        objectCaching: false,
    };

    constructor(
        @Inject(PLATFORM_ID) private platformId: object,
        private customImageUploadService: CustomImageUploadService,
        private activeService: ActiveService,
        private notificationService: NotificationService,
        private fontLoaderService: FontLoaderService,
        private dataService: DataService,
        private logger: NGXLogger,
        private cd: ChangeDetectorRef
    ) {
        this.isBrowser = isPlatformBrowser(this.platformId);
    }

    ngOnInit(): void {
        this.logger.debug('ImageCustomizerModalComponent initializing with pattern:', this.printingPattern, this.paidOptions, this.messageOptions);
        if (!this.printingPattern) {
            this.logger.error('ImageCustomizerModalComponent requires printingPattern data!');
            this.resolveWith(undefined);
            return;
        }
        this.frameStates = this.printingPattern.imageFrames.map((frame, index) => ({
            frameIndex: index,
            clipRect: null,
            frameRect: null,
            image: null,
            name: frame.name ? frame.name : `Photo ${index + 1}`,
            uploadedImageUrl: null,
            isLoading: false,
        } as any));
        if (this.messageOptions?.length) {
            const n = this.messageOptions.length;
            this.messageInputs = Array(n).fill('');
            this.messageValidation = {};
        }
        if (this.paidOptions?.length) {
            const n = this.paidOptions.length;
            this.paidSelections = Array(n).fill(null);
            this.paidValidation = {};
        }
        this.buildOrderedOptions();
        if (this.printingPattern?.backgroundImages?.length) {
            this.selectedBackgroundIndex = 0;
        }
        this.initTextFrameGroups();
        this.activeService.activeOrder$.subscribe((order:any) => {
            if (order) {
                this.activeOrder = order;
                this.logger.debug(`Active order:${JSON.stringify(order)}`);
            } else {
                this.logger.error('No active order found!');            }
        });
        this.messagesRequired = this.messageOptions?.some((m,i)=>m.required);
        if(this.isMobile) {
            this.buttonText = "OK";
        }
        Object.assign(fabric.FabricObject.prototype, this.fabricDefaults);
    }

    private buildOrderedOptions(): void {
        this.orderedOptions = [];
        if (this.customizationOptions?.length) {
            let msgIdx = 0;
            let paidIdx = 0;
            for (const opt of this.customizationOptions) {
                if (opt.type === 'custom-message') {
                    this.orderedOptions.push({ kind: 'message', index: msgIdx++ });
                } else if (opt.type === 'custom-options') {
                    this.orderedOptions.push({ kind: 'paid', index: paidIdx++ });
                } else if (opt.type === 'custom-text-frames') {
                    // Standalone text-overlay option — its overlay groups render
                    // (and submit) inline here, matched by option id. `index` is
                    // unused; the groups are looked up by optionId.
                    this.orderedOptions.push({ kind: 'textframe', index: 0, optionId: opt.id });
                }
            }
        } else {
            this.paidOptions?.forEach((_, i) => this.orderedOptions.push({ kind: 'paid', index: i }));
            this.messageOptions?.forEach((_, i) => this.orderedOptions.push({ kind: 'message', index: i }));
        }
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            if (this.printingPattern) {
                this.initializeCanvases();
                this.loadSelectedBackground();
            }
        });
    }

    get imageButtonsIndices(): number[] {
        return this.frameStates.map(fs => fs.frameIndex);
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        if (this.editorCanvas) {
            this.editorCanvas.dispose();
        }
    }

    onMessageChange(index: number, value: string): void {
        this.messageInputs[index] = value;
        if (this.messageValidation[index]) {
            this.messageValidation[index] = false;
        }
        this.cd.markForCheck();
    }

    onPaidChange(idx: number, item: PaidOptionItem | null): void {
        this.paidSelections[idx] = item;
        if (this.paidValidation[idx]) {
            this.paidValidation[idx] = false;
        }
        this.cd.markForCheck();
        this.nudgeRepaint();
    }

    /**
     * Workaround for a prod-build "modal goes blank after selecting an
     * option-list item" bug. The content stays in the DOM with a healthy
     * height, but Chromium skips repainting the composited modal body after the
     * OnPush change-detection pass (only reproduces in the deployed prod build,
     * not local dev — a compositing difference). The unpainted content lives in
     * descendant compositing layers (the overflow-y-auto scroll panels + the
     * fabric canvas), so an ancestor opacity change isn't enough; momentarily
     * detaching the container from the render tree (display:none → reflow →
     * restore) forces every descendant layer to repaint — equivalent to the
     * manual reflow DevTools triggers. Runs after CD (rAF); the none state is
     * set and restored within one frame, so it never paints (no flicker).
     *
     * `display:none` removes the subtree from the accessibility tree and makes
     * Chromium drop focus (it is NOT restored when display comes back). A
     * keyboard / screen-reader user selecting a radio with Space would lose
     * their place in the group, so we capture the focused element and re-focus
     * it after the reflow (preventScroll so the repaint doesn't jump the page).
     */
    private nudgeRepaint(): void {
        if (!this.isBrowser) return;
        const el = this.modalScroll?.nativeElement;
        if (!el || typeof requestAnimationFrame !== 'function') return;
        requestAnimationFrame(() => {
            const active = document.activeElement as HTMLElement | null;
            const focusInside = !!active && active !== document.body && el.contains(active);
            const prevDisplay = el.style.display;
            el.style.display = 'none';
            void el.offsetHeight; // force synchronous reflow while detached
            el.style.display = prevDisplay;
            if (focusInside && typeof active!.focus === 'function') {
                active!.focus({ preventScroll: true });
            }
        });
    }

    get totalPrice(): number {
        const addons = this.paidSelections.reduce(
          (sum, item) => sum + (item?.price ?? 0),
          0,
        );
        return this.variantPrice + addons;
    }

    initializeCanvases(): void {
        if (!this.editorCanvasEl?.nativeElement) {
             this.logger.error('Canvas element not found!');
             return;
        }

        this.editorCanvas = new fabric.Canvas(this.editorCanvasEl.nativeElement, {
            width: this.printingPattern?.canvasWidth,
            height: this.printingPattern?.canvasHeight,
            backgroundColor: '#f0f0f0',
            selection: false,
            // Honour the real z-order even for the selected object, so text
            // overlays (kept on top via bringTextFramesToFront) are never hidden
            // behind a photo while it has focus / is being resized or moved.
            preserveObjectStacking: true,
        });
        this.originalW = this.printingPattern?.canvasWidth || 0;
        this.originalH = this.printingPattern?.canvasHeight || 0;
        this.fitCanvasToContainer();
        this.frameStates.forEach((state, index) => {
            const frameData = this.printingPattern?.imageFrames[index];
            const rectConfig = {
                left: (frameData?.positionX || 0) / this.canvasScale,
                top: (frameData?.positionY || 0) / this.canvasScale,
                width: (frameData?.sizeX || 0) / this.canvasScale,
                height: (frameData?.sizeY || 0) / this.canvasScale,
                selectable: false,
                evented: false,
                absolutePositioned: true,
            };
            state.clipRect = new fabric.Rect({
                ...rectConfig,
                fill: 'transparent',
            });
            state.frameRect = new fabric.Rect({
                ...rectConfig,
                fill: `rgba(255, 255, 255, ${this.transparency})`,
                strokeWidth: 1,
                strokeDashArray: [5, 5]
            });
            this.editorCanvas.add(state.frameRect);
            const labelText = frameData?.label || '';
            const label = new fabric.Textbox(labelText, {
                originX: 'center',
                originY: 'center',
                fontSize: (frameData?.labelFontSize ?? DEFAULT_LABEL_FONT_SIZE) / this.canvasScale,
                fill: frameData?.labelColor ?? DEFAULT_LABEL_COLOR,
                selectable: false,
                evented: false,
            });
            this.fitLabelToFrame(state.frameRect, label);
            this.editorCanvas.add(label);
        });
        // Allow selecting images and text frames when clicking
        this.editorCanvas.on('mouse:down', (options) => {
             if (options.target) {
                 const isImage = this.frameStates.some(fs => fs.image === options.target);
                 const isTextbox = this.textGroupStates.some(gs =>
                     gs.frameStates.some(fs => fs.textbox === options.target));
                 if (isImage || isTextbox) return;
             }
             const pointer = this.editorCanvas.getPointer(options.e);
             const targetState = this.frameStates.find(fs => fs.frameRect.containsPoint(pointer));

             if (targetState?.image) {
                this.editorCanvas.setActiveObject(targetState.image);
             } else {
                this.editorCanvas.discardActiveObject();
             }
             this.editorCanvas.renderAll();
        });

        this.editorCanvas.on('object:moving', (e) => {
            const obj = e.target;
            if (!obj) return;
            const isTextFrame = this.textGroupStates.some(gs =>
                gs.frameStates.some(fs => fs.textbox === obj));
            if (!isTextFrame) return;
            const halfW = (obj.width || 0) * (obj.scaleX || 1) / 2;
            const fullH = (obj.height || 0) * (obj.scaleY || 1);
            // Nested overlays clamp to the owning frame's clipRect; standalone
            // overlays fall back to the full canvas dims.
            const boundary = this.getBoundaryForTextbox(obj);
            const leftMin = boundary?.left ?? 0;
            const topMin = boundary?.top ?? 0;
            const cw = boundary?.width ?? this.originalW / this.canvasScale;
            const ch = boundary?.height ?? this.originalH / this.canvasScale;
            const leftMax = leftMin + cw;
            const topMax = topMin + ch;
            if (obj.left! - halfW < leftMin) obj.set('left', leftMin + halfW);
            if (obj.left! + halfW > leftMax) obj.set('left', leftMax - halfW);
            if (obj.top! < topMin) obj.set('top', topMin);
            if (obj.top! + fullH > topMax) obj.set('top', topMax - fullH);
        });

        this.editorCanvas.on('mouse:down', (e) => {
            const transform = (this.editorCanvas as any)._currentTransform;
            if (transform?.corner === 'mtr' && e.target) {
                const now = Date.now();
                if (this.lastMtrClickTarget === e.target && now - this.lastMtrClickTime < 400) {
                    e.target.set('angle', 0);
                    e.target.setCoords();
                    const isTextFrame = this.textGroupStates.some(gs =>
                        gs.frameStates.some(fs => fs.textbox === e.target));
                    if (isTextFrame) {
                        // Route through the boundary-aware constraint.
                        const boundary = this.getBoundaryForTextbox(e.target);
                        this.constrainToBoundary(e.target, boundary);
                    }
                    this.editorCanvas.renderAll();
                    this.lastMtrClickTime = 0;
                    this.lastMtrClickTarget = null;
                } else {
                    this.lastMtrClickTime = now;
                    this.lastMtrClickTarget = e.target;
                }
            } else {
                this.lastMtrClickTime = 0;
                this.lastMtrClickTarget = null;
            }
        });

        this.addTextFramesToCanvas();
        this.isCanvasReady = true;
        this.editorCanvas.renderAll();
        this.cd.markForCheck();
        this.logger.debug('Fabric canvas initialized.');
    }

    private initTextFrameGroups(): void {
        let gi = 0;
        const pushGroup = (
            raw: TextFrameGroupConfig,
            sourceOptionId: string,
            parentImageFrameName?: string,
            parentImageFrameIndex?: number,
            sourceOptionLabel?: string,
            sourceOptionDescription?: string,
        ) => {
            const config = {
                ...raw,
                allowedColors: (raw.allowedColors || []).map(normalizeTextFrameColor),
            };
            const defaultColorEntry = config.allowedColors.find(c => c.hex === config.defaultColor);
            this.textGroupStates.push({
                groupIndex: gi++,
                config,
                sourceOptionId,
                selectedFont: config.defaultFont,
                selectedColor: config.defaultColor,
                selectedColorName: defaultColorEntry?.name || config.defaultColor,
                selectedFontSize: config.defaultFontSize || config.fontSizeOptions?.[0] || 16,
                effectiveFonts: this.fetchFontCatalogForGroup(config),
                frameStates: config.frames.map((frame, fi) => ({
                    frameIndex: fi,
                    frameData: frame,
                    textbox: null as any,
                    content: '',
                    displayText: '',
                    isValid: !frame.required,
                    isOverLineLimit: false,
                })),
                parentImageFrameName,
                parentImageFrameIndex,
                sourceOptionLabel,
                sourceOptionDescription,
            });
        };

        // Nested overlay groups owned by an image-frame on the print template.
        // Drag/resize boundary is the frame's clipRect (set up by
        // initializeCanvases), not the canvas.
        if (this.printingPattern?.imageFrames?.length) {
            this.printingPattern.imageFrames.forEach((frame, frameIndex) => {
                const nested = frame.textFrameGroups ?? [];
                // Use the same fallback frame name as frameStates / submitted image
                // labels so nested overlays under unnamed frames stay attributable
                // (and same-named groups across blank frames don't collapse).
                const frameName = frame.name ? frame.name : `Photo ${frameIndex + 1}`;
                // Default a blank group name to "{Frame Name} Text Overlay"; number
                // the 2nd/3rd unnamed group under the same frame so they stay distinct.
                let unnamedCount = 0;
                for (const raw of nested) {
                    let group = raw;
                    if (!raw.name || !raw.name.trim()) {
                        unnamedCount++;
                        const suffix = unnamedCount > 1 ? ` ${unnamedCount}` : '';
                        group = { ...raw, name: `${frameName} Text Overlay${suffix}` };
                    }
                    pushGroup(group, this.printingPattern!.optionId || '', frameName, frameIndex);
                }
            });
        }

        // Standalone overlay groups from a sibling `custom-text-frames` option
        // type. No frame attribution; boundary is the full canvas. The option's
        // label/description become the section heading (consistent with other
        // customization options).
        if (this.textFrameOptions?.length) {
            for (const opt of this.textFrameOptions) {
                const groups = opt.data.textFrameGroups ?? [];
                for (const raw of groups) {
                    pushGroup(raw, opt.optionId, undefined, undefined, opt.data.label, opt.data.description);
                }
            }
        }

        // Pre-split into nested vs standalone entries ONCE, preserving each
        // group's original index and a stable wrapper identity. Bound to *ngFor
        // in the template; building them here (not via a getter) keeps the rows
        // from being torn down/recreated on every change-detection pass.
        this.rebuildTextGroupEntries();

        if (!this.textGroupStates.length) return;
        this.isFontLoading = true;
        this.loadServerFontCatalog().then(() => {
            for (const gs of this.textGroupStates) {
                gs.effectiveFonts = this.fetchFontCatalogForGroup(gs.config);
            }
            return this.loadTextFrameFonts();
        }).catch(err => {
            this.logger.warn('Failed to load text frame fonts', err);
        }).finally(() => {
            this.isFontLoading = false;
            this.cd.markForCheck();
        });
    }

    private async loadServerFontCatalog(): Promise<void> {
        if (this.serverFontCatalog) return;
        try {
            const res: any = await this.dataService.query(GET_PRINT_FONT_CATALOG).pipe(take(1)).toPromise();
            if (res?.printFontCatalog?.length) {
                this.serverFontCatalog = res.printFontCatalog;
            }
        } catch (e) {
            this.logger.warn('Failed to load server font catalog, using hardcoded fallback', e);
        }
    }

    private async loadTextFrameFonts(): Promise<void> {
        const allFonts = new Map<string, string>();
        for (const gs of this.textGroupStates) {
            const catalog = await this.fetchFontCatalogForGroup(gs.config);
            for (const entry of catalog) {
                allFonts.set(entry.family, entry.googleFontsUrl);
            }
        }
        const entries = Array.from(allFonts.entries()).map(([family, googleFontsUrl]) => ({ family, googleFontsUrl }));
        if (entries.length) {
            await this.fontLoaderService.loadFonts(entries);
            if (this.editorCanvas) {
                this.editorCanvas.renderAll();
            }
            this.cd.markForCheck();
        }
    }

    private static readonly FALLBACK_FONT_CATALOG: { family: string; googleFontsUrl: string }[] = [
        { family: 'Courgette', googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Courgette&display=swap' },
        { family: 'Pacifico', googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Pacifico&display=swap' },
        { family: 'Dancing Script', googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Dancing+Script&display=swap' },
        { family: 'Great Vibes', googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap' },
        { family: 'Sacramento', googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Sacramento&display=swap' },
        { family: 'Lobster', googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Lobster&display=swap' },
        { family: 'Roboto', googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Roboto&display=swap' },
        { family: 'Open Sans', googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Open+Sans&display=swap' },
        { family: 'Montserrat', googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Montserrat&display=swap' },
        { family: 'Poppins', googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Poppins&display=swap' },
        { family: 'Oswald', googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Oswald&display=swap' },
        { family: 'Bebas Neue', googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Bebas+Neue&display=swap' },
        { family: 'Raleway', googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Raleway&display=swap' },
        { family: 'Merriweather', googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Merriweather&display=swap' },
        { family: 'Playfair Display', googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Playfair+Display&display=swap' },
        { family: 'Lora', googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Lora&display=swap' },
        { family: 'Libre Baskerville', googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Libre+Baskerville&display=swap' },
        { family: 'Cinzel', googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Cinzel&display=swap' },
        { family: 'Alfa Slab One', googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Alfa+Slab+One&display=swap' },
        { family: 'Permanent Marker', googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Permanent+Marker&display=swap' },
        { family: 'Bangers', googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Bangers&display=swap' },
    ];

    private fetchFontCatalogForGroup(config: TextFrameGroupConfig): { family: string; googleFontsUrl: string }[] {
        const catalog = this.serverFontCatalog ?? ImageCustomizerModalComponent.FALLBACK_FONT_CATALOG;
        if (config.allowedFonts.length === 0) return catalog;
        return catalog.filter(f => config.allowedFonts.includes(f.family));
    }

    /**
     * Vertical seed position for a nested overlay textbox: frames in a group are
     * distributed evenly down the owning frame rect so multiple overlays don't
     * stack at the center. A single frame lands at the rect's vertical center.
     */
    private nestedFrameInitialTop(rectTop: number, rectHeight: number, frameIndex: number, frameCount: number): number {
        return rectTop + (rectHeight * (frameIndex + 1)) / (frameCount + 1);
    }

    /**
     * Plan vertical seed positions for nested overlays so that ALL textboxes
     * owned by one image frame — across sibling groups, not just within a single
     * group — are distributed down the frame rect instead of stacking at center.
     *
     * For each group (in the given order) returns the per-frame `total` (count of
     * textboxes owned by that image frame) and the `seedIndices` its frames take
     * within that frame's sequence. Standalone groups (no parentImageFrameIndex)
     * get empty seedIndices and are positioned by the canvas-centered fallback.
     */
    static buildNestedSeedPlan(
        groups: Array<{ parentImageFrameIndex: number | null; frameCount: number }>,
    ): Array<{ total: number; seedIndices: number[] }> {
        const totalByFrame = new Map<number, number>();
        for (const g of groups) {
            if (g.parentImageFrameIndex != null) {
                totalByFrame.set(g.parentImageFrameIndex, (totalByFrame.get(g.parentImageFrameIndex) ?? 0) + g.frameCount);
            }
        }
        const nextSeedByFrame = new Map<number, number>();
        return groups.map(g => {
            if (g.parentImageFrameIndex == null) {
                return { total: g.frameCount, seedIndices: [] };
            }
            const total = totalByFrame.get(g.parentImageFrameIndex) ?? g.frameCount;
            let seed = nextSeedByFrame.get(g.parentImageFrameIndex) ?? 0;
            const seedIndices: number[] = [];
            for (let i = 0; i < g.frameCount; i++) {
                seedIndices.push(seed++);
            }
            nextSeedByFrame.set(g.parentImageFrameIndex, seed);
            return { total, seedIndices };
        });
    }

    private addTextFramesToCanvas(): void {
        if (!this.textGroupStates.length || !this.editorCanvas) return;
        const canvasW = this.editorCanvas.getWidth();
        const canvasH = this.editorCanvas.getHeight();
        let offsetY = canvasH * 0.1;
        // Seed nested overlays across ALL textboxes owned by the same image frame
        // — counted across sibling groups, not reset per group — so two one-frame
        // groups on the same frame don't both land at the rect's vertical center.
        const seedPlan = ImageCustomizerModalComponent.buildNestedSeedPlan(
            this.textGroupStates.map(g => ({
                parentImageFrameIndex: g.parentImageFrameIndex ?? null,
                frameCount: g.frameStates.length,
            })),
        );
        let groupIdx = -1;
        for (const gs of this.textGroupStates) {
            groupIdx++;
            // Nested overlays seed their initial position from the owning
            // frame's clipRect; standalone overlays use a canvas-centered seed.
            // The frame outline is always present (added in initializeCanvases)
            // regardless of upload state, so even empty frames produce a valid
            // boundary and the overlay still renders.
            const parentFrameState =
                gs.parentImageFrameIndex != null
                    ? this.frameStates[gs.parentImageFrameIndex]
                    : null;
            const parentRect = parentFrameState?.clipRect;
            const frameCount = gs.frameStates.length;
            let frameSeq = -1;
            for (const fs of gs.frameStates) {
                frameSeq++;
                const initialLeft = parentRect
                    ? (parentRect.left ?? 0) + (parentRect.width ?? 0) / 2
                    : canvasW / 2 / this.canvasScale;
                // Distribute nested overlays evenly down the owning frame rect so
                // they don't all stack at the center. Indices/counts span every
                // group owned by the frame (see buildNestedSeedPlan), so sibling
                // groups interleave instead of overlapping. A frame with a single
                // overlay lands at the center (matching the prior behavior).
                let initialTop: number;
                if (parentRect && gs.parentImageFrameIndex != null) {
                    const plan = seedPlan[groupIdx];
                    initialTop = this.nestedFrameInitialTop(parentRect.top ?? 0, parentRect.height ?? 0, plan.seedIndices[frameSeq], plan.total);
                } else if (parentRect) {
                    initialTop = this.nestedFrameInitialTop(parentRect.top ?? 0, parentRect.height ?? 0, fs.frameIndex, frameCount);
                } else {
                    initialTop = offsetY / this.canvasScale;
                }
                const tb = new fabric.Text(fs.content, {
                    left: initialLeft,
                    top: initialTop,
                    originX: 'center',
                    fontSize: gs.selectedFontSize / this.canvasScale,
                    fontFamily: gs.selectedFont,
                    fill: gs.selectedColor,
                    textAlign: fs.frameData.textAlign,
                    backgroundColor: 'transparent',
                    editable: false,
                    selectable: true,
                    evented: true,
                    visible: false,
                    lockUniScaling: true,
                    transparentCorners: false,
                    cornerColor: 'blue',
                    cornerStyle: 'circle' as const,
                    borderColor: 'blue',
                    borderDashArray: [3, 3],
                    objectCaching: false,
                });
                tb.setControlsVisibility({ mt: false, mb: false, ml: false, mr: false });
                // Stash the owning frame on the fabric object so per-event
                // handlers can look up the boundary cheaply. Standalone overlays
                // leave this undefined.
                (tb as any).parentFrame = parentFrameState ?? null;
                (tb as any).parentImageFrameName = gs.parentImageFrameName ?? null;
                tb.on('modified', () => {
                    this.refitTextAfterResize(tb);
                    const boundary = this.getBoundaryForTextbox(tb);
                    this.constrainToBoundary(tb, boundary);
                });
                fs.textbox = tb;
                this.editorCanvas.add(tb);
                offsetY += 60;
            }
        }
    }

    private bringTextFramesToFront(): void {
        for (const gs of this.textGroupStates) {
            for (const fs of gs.frameStates) {
                if (fs.textbox) {
                    this.editorCanvas.bringObjectToFront(fs.textbox);
                }
            }
        }
    }

    onGroupFontChange(groupIndex: number, family: string): void {
        const gs = this.textGroupStates[groupIndex];
        if (!gs) return;
        gs.selectedFont = family;
        gs.frameStates.forEach(fs => {
            if (fs.textbox) {
                fs.textbox.set('fontFamily', family);
                this.refitTextboxWithinBoundary(fs.textbox, gs.config.autoFit && !!fs.content);
            }
        });
        this.editorCanvas?.renderAll();
        this.cd.markForCheck();
    }

    onGroupColorChange(groupIndex: number, colorOption: TextFrameColorOption): void {
        const gs = this.textGroupStates[groupIndex];
        if (!gs) return;
        gs.selectedColor = colorOption.hex;
        gs.selectedColorName = colorOption.name || colorOption.hex;
        gs.frameStates.forEach(fs => {
            fs.textbox?.set('fill', colorOption.hex);
        });
        this.editorCanvas?.renderAll();
        this.cd.markForCheck();
    }

    onGroupFontSizeChange(groupIndex: number, size: number): void {
        const gs = this.textGroupStates[groupIndex];
        if (!gs) return;
        gs.selectedFontSize = size;
        gs.frameStates.forEach(fs => {
            if (fs.textbox) {
                fs.textbox.set('fontSize', size / this.canvasScale);
                this.refitTextboxWithinBoundary(fs.textbox, gs.config.autoFit);
            }
        });
        this.editorCanvas?.renderAll();
        this.cd.markForCheck();
    }

    onTextContentChange(groupIndex: number, frameIndex: number, value: string): void {
        const gs = this.textGroupStates[groupIndex];
        const fs = gs?.frameStates[frameIndex];
        if (!fs) return;
        fs.content = value;
        fs.isValid = !fs.frameData.required || !!value.trim();
        const maxLines = fs.frameData.maxLines || 3;
        const lines = value.split('\n');
        fs.isOverLineLimit = lines.length > maxLines;
        const displayText = fs.isOverLineLimit ? lines.slice(0, maxLines).join('\n') : value;
        fs.displayText = displayText;
        if (fs.textbox) {
            fs.textbox.set('text', displayText);
            fs.textbox.set('visible', !!displayText);
            this.refitTextboxWithinBoundary(fs.textbox, gs.config.autoFit && !!displayText);
        }
        this.editorCanvas?.renderAll();
        this.cd.markForCheck();
    }

    /**
     * Re-fit a textbox inside its boundary after its text/font/size changed.
     * Nested overlays clamp to the owning image-frame's clipRect; standalone
     * overlays clamp to the canvas (via `constrainToBoundary` + `getBoundaryForTextbox`).
     *
     * Scale is reset to 1 first so the box re-evaluates from its natural,
     * font-size-driven dimensions — this lets it grow back when the user deletes
     * text after a previous overflow clamped it down. Manual drag-resizes are
     * already baked into `fontSize` by `refitTextAfterResize`, so resetting scale
     * here never discards a user resize.
     */
    private refitTextboxWithinBoundary(textbox: fabric.FabricText, autoFit: boolean): void {
        textbox.set({ scaleX: 1, scaleY: 1 });
        textbox.initDimensions();
        if (autoFit) {
            this.autoFitText(textbox);
        }
        this.constrainToBoundary(textbox, this.getBoundaryForTextbox(textbox));
    }

    private autoFitText(text: fabric.FabricText): void {
        // Auto-fit against the textbox's owning boundary: the frame's clipRect for
        // nested overlays, the full canvas for standalone overlays. Falls back to
        // canvas dims when no nested boundary resolves (back-compat).
        const boundary = this.getBoundaryForTextbox(text);
        const boundW = boundary ? boundary.width : this.originalW / this.canvasScale;
        const boundH = boundary ? boundary.height : this.originalH / this.canvasScale;
        const maxW = boundW * 0.9;
        const maxH = boundH * 0.9;
        let lo = 8, hi = 200;
        while (lo < hi) {
            const mid = Math.ceil((lo + hi) / 2);
            text.set('fontSize', mid / this.canvasScale);
            text.initDimensions();
            if ((text.width ?? 0) <= maxW && (text.height ?? 0) <= maxH) {
                lo = mid;
            } else {
                hi = mid - 1;
            }
        }
        text.set('fontSize', lo / this.canvasScale);
        text.initDimensions();
        text.setCoords();
    }

    private refitTextAfterResize(text: fabric.FabricText): void {
        const sx = text.scaleX || 1;
        if (Math.abs(sx - 1) < 0.01) return;
        const newFontSize = (text.fontSize || 16) * sx;
        text.set({ fontSize: newFontSize, scaleX: 1, scaleY: 1 });
        text.initDimensions();
        text.setCoords();
    }

    /**
     * Deprecated alias — forwards to `constrainToBoundary(obj)`, which clamps to
     * the canvas when no boundary is given. New call sites should call
     * `constrainToBoundary` directly, passing the owning frame's rect for nested
     * overlays.
     */
    private constrainToCanvas(obj: fabric.FabricObject): void {
        this.constrainToBoundary(obj);
    }

    /**
     * Clamp the object's position + scale inside the given boundary rect.
     *
     * When `boundaryRect` is omitted the boundary defaults to the full canvas
     * (`this.originalW / this.canvasScale` × `this.originalH / this.canvasScale`,
     * origin (0, 0)) — used by standalone `custom-text-frames` overlays. When
     * `boundaryRect` is provided we clamp to that rect — used by nested overlays
     * whose owning frame's clipRect bounds the textbox.
     */
    private constrainToBoundary(
        obj: fabric.FabricObject,
        boundaryRect?: { left: number; top: number; width: number; height: number },
    ): void {
        const cw = boundaryRect ? boundaryRect.width : this.originalW / this.canvasScale;
        const ch = boundaryRect ? boundaryRect.height : this.originalH / this.canvasScale;
        const leftMin = boundaryRect ? boundaryRect.left : 0;
        const topMin = boundaryRect ? boundaryRect.top : 0;
        const leftMax = leftMin + cw;
        const topMax = topMin + ch;

        // Measure the object by its TRANSFORMED bounding rect (axis-aligned box
        // after scale + rotation), so a rotated overlay is clamped by the space
        // it actually occupies — not its unrotated width/height.
        obj.setCoords();
        let br = obj.getBoundingRect();

        // Scale down uniformly if the footprint overflows either boundary dim.
        if (br.width > cw || br.height > ch) {
            const ratio = Math.min(cw / br.width, ch / br.height);
            obj.set({ scaleX: (obj.scaleX || 1) * ratio, scaleY: (obj.scaleY || 1) * ratio });
            obj.setCoords();
            br = obj.getBoundingRect();
        }

        // Shift the object so its bounding rect sits inside the boundary. We move
        // obj.left/top by the same delta as the bounding box, which is correct for
        // any rotation/origin (the anchor-to-bbox offset is constant under a shift).
        let dx = 0;
        let dy = 0;
        if (br.left < leftMin) dx = leftMin - br.left;
        else if (br.left + br.width > leftMax) dx = leftMax - (br.left + br.width);
        if (br.top < topMin) dy = topMin - br.top;
        else if (br.top + br.height > topMax) dy = topMax - (br.top + br.height);
        if (dx !== 0 || dy !== 0) {
            obj.set({ left: (obj.left || 0) + dx, top: (obj.top || 0) + dy });
            obj.setCoords();
        }
        this.editorCanvas?.renderAll();
    }

    /**
     * Resolve the per-overlay boundary rect for a textbox. Returns the owning
     * frame's clipRect bounds when the overlay is nested; returns `undefined` for
     * standalone overlays so `constrainToBoundary` falls back to the canvas dims.
     */
    private getBoundaryForTextbox(
        obj: fabric.FabricObject,
    ): { left: number; top: number; width: number; height: number } | undefined {
        const owningGroup = this.textGroupStates.find(gs =>
            gs.frameStates.some(fs => fs.textbox === obj),
        );
        if (!owningGroup || owningGroup.parentImageFrameIndex == null) return undefined;
        const fs = this.frameStates[owningGroup.parentImageFrameIndex];
        const rect = fs?.clipRect;
        if (!rect) return undefined;
        return {
            left: rect.left ?? 0,
            top: rect.top ?? 0,
            width: rect.width ?? 0,
            height: rect.height ?? 0,
        };
    }

    getEffectiveFontsForGroup(groupIndex: number): { family: string; googleFontsUrl: string }[] {
        const gs = this.textGroupStates[groupIndex];
        if (!gs) return [];
        return this.fetchFontCatalogForGroup(gs.config);
    }

    loadSelectedBackground(): void {
        const backgroundImage = this.printingPattern?.backgroundImages?.[this.selectedBackgroundIndex];
        const url = backgroundImage?.asset?.imageUrl || backgroundImage?.asset?.preview || null;
        this.updateBackgroundImage(url);
    }

    onBackgroundChange(idx: number): void {
        if (idx === this.selectedBackgroundIndex) return;
        this.selectedBackgroundIndex = idx;
        this.loadSelectedBackground();
    }

    private isValidFileType(file: File): boolean {
        // Some browsers leave type empty; in that case we don’t block.
        if (!file.type) {
            return true;
        }
        const type = file.type.toLowerCase();
        return this.allowedMimeTypes.includes(type);
    }
    private async getHeic2any(): Promise<any> {
        if (!this.isBrowser) {
          throw new Error('heic2any can only run in the browser');
        }

        if (!this.heic2anyPromise) {
          this.heic2anyPromise = import('heic2any').then(m => m.default ?? m);
        }
        return this.heic2anyPromise;
    }
    private isHeicFile(file: File): boolean {
        const type = file.type?.toLowerCase() || '';
        if (type === 'image/heic' || type === 'image/heif') return true;
        // Fallback to extension check if type is empty/wrong
        return /\.hei[cf]$/i.test(file.name);
    }
    private isValidFileSize(file: File): boolean {
        const maxBytes = this.maxImageSizeMB * 1024 * 1024;
        return file.size <= maxBytes;
    }
    private warnIfLowResolution(img: fabric.Image): void {
        const minW = this.printingPattern?.minWidth;
        const minH = this.printingPattern?.minHeight;
        if (!minW || !minH) {
            return;
        }
        const size = img.getOriginalSize();
        const iw = size.width || img.width || 0;
        const ih = size.height || img.height || 0;
        if (!iw || !ih) {
            return;
        }
        if (iw < minW || ih < minH) {
            this.notificationService.info(
                `The photo you selected is ${iw}×${ih}, which is below the suggested ${minW}×${minH}. ` +
                `It may look a bit blurry when printed.`
            ).subscribe();
        }
    }
    private async convertHeicToJpeg(file: File): Promise<File> {
        if (!this.isBrowser) {
            throw new Error('HEIC conversion can only run in the browser');
        }
        try {
            const heic2any = await this.getHeic2any();
            const result = await heic2any({
                blob: file,
                toType: 'image/jpeg'
            });
            const blob = Array.isArray(result) ? result[0] as Blob : result as Blob;
            if (!blob || !blob.size) {
                throw new Error('HEIC_CONVERT_EMPTY_BLOB');
            }
            const basename = file.name.replace(/\.hei[cf]$/i, '') || 'photo';
            return new File([blob], `${basename}.jpg`, { type: 'image/jpeg' });
        } catch (err) {
            this.logger.error('HEIC conversion failed', err);
            throw err;
        }
    }
    private async createPreviewBlob(
        img: fabric.Image,
        maxSize: number = 100,
    ): Promise<{ blob: Blob; dataUrl: string }> {
        const el = img.getElement() as HTMLImageElement | undefined;
        const origSize = img.getOriginalSize();
        const natW = el?.naturalWidth || origSize.width || img.width || 0;
        const natH = el?.naturalHeight || origSize.height || img.height || 0;
        if (!natW || !natH) {
            this.logger.error('[Preview] Cannot determine image dimensions for preview.');
            throw new Error('PREVIEW_DIMENSION_ERROR');
        }
        const scale = Math.min(maxSize / natW, maxSize / natH, 1);
        const thumbW = Math.round(natW * scale);
        const thumbH = Math.round(natH * scale);
        if (!thumbW || !thumbH) {
            this.logger.error('[Preview] Computed thumbnail size is zero.');
            throw new Error('PREVIEW_SIZE_ZERO');
        }
        const canvas = document.createElement('canvas');
        canvas.width = thumbW;
        canvas.height = thumbH;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            this.logger.error('[Preview] 2D context not available.');
            throw new Error('PREVIEW_CONTEXT_ERROR');
        }
        ctx.drawImage(
            el!,
            0, 0, natW, natH,
            0, 0, thumbW, thumbH,
        );
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        if (!dataUrl || dataUrl === 'data:,') {
            this.logger.error('[Preview] Invalid preview data URL.');
            throw new Error('PREVIEW_DATAURL_ERROR');
        }
        // Convert dataUrl → Blob; if this fails, treat as hard error
        try {
            const resp = await fetch(dataUrl);
            if (!resp.ok) {
                this.logger.error('[Preview] Fetch(dataUrl) failed with status', resp.status);
                throw new Error('PREVIEW_FETCH_ERROR');
            }
            const blob = await resp.blob();
            if (!blob || !blob.size) {
                this.logger.error('[Preview] Blob is empty.');
                throw new Error('PREVIEW_BLOB_EMPTY');
            }
            return { blob, dataUrl };
        } catch (err) {
            this.logger.error('[Preview] Failed to create preview blob.', err);
            throw new Error('PREVIEW_BLOB_ERROR');
        }
    }

    private fitCanvasToContainer() {
        if (!this.editorCanvas || !this.canvasContainer?.nativeElement) return;
        const cw = this.canvasContainer.nativeElement.clientWidth;
        const ch = this.canvasContainer.nativeElement.clientHeight;
        this.canvasScale = Math.min(cw / this.originalW, ch / this.originalH);
        this.editorCanvas.setDimensions(
          {
            width: this.originalW * this.canvasScale,
            height: this.originalH * this.canvasScale,
          },
          { cssOnly: true }
        );
        const vt = this.editorCanvas.viewportTransform!;
        vt[0] = vt[3] = this.canvasScale;
        vt[4] = vt[5] = 0;
        this.editorCanvas.setViewportTransform(vt);
        this.editorCanvas.renderAll();
    }

    private applyBackgroundScaling(img: fabric.Image): void {
        if (!this.editorCanvas || !img.width || !img.height) {
            this.logger.warn('[BG] Cannot scale – canvas or image size unknown.');
            return;
        }
        const cw  = this.originalW / this.canvasScale;
        const ch  = this.originalH / this.canvasScale;
        const iw  = img.getOriginalSize().width  || img.width!;
        const ih  = img.getOriginalSize().height || img.height!;

        if (!iw || !ih) {
            this.logger.warn('[BG] Image original dimension is zero.');
            return;
        }

        const scale = Math.max(cw / iw, ch / ih);
        img.set({
            scaleX: scale,
            scaleY: scale,
            originX: 'center',
            originY: 'center',
            left: cw / 2,
            top:  ch / 2,
            selectable: false,
            evented:    false,
        });
    }

    private scaleImageToFrame(img: fabric.Image, frameRect: fabric.Rect): void {
        const iw = img.getOriginalSize().width  || img.width!;
        const ih = img.getOriginalSize().height || img.height!;
        if (!iw || !ih) {
          this.logger.warn('[Frame] Zero image dimension – cannot scale.');
          return;
        }
        const scale = Math.max(frameRect.width!  / iw, frameRect.height! / ih);
        img.set({
          scaleX: scale,
          scaleY: scale,
          left:  frameRect.left! + frameRect.width!  / 2,
          top:   frameRect.top!  + frameRect.height! / 2,
          originX: 'center',
          originY: 'center',
          clipPath: frameRect,
          lockUniScaling: true,
          centeredScaling: true,
          ...this.fabricDefaults,
        });
        img.setControlsVisibility({ mt: false, mb: false, ml: false, mr: false });
        img.on('scaling', () => {
            const sx = img.scaleX || 1;
            const sy = img.scaleY || 1;
            if (Math.abs(sx - sy) > 0.001) {
                const s = Math.max(sx, sy);
                img.set({ scaleX: s, scaleY: s });
            }
        });
    }

    private fitLabelToFrame(frameRect: fabric.Rect, label: fabric.Textbox): void {
        const frameW = frameRect.width!;
        const targetW = frameW * this.maxLabelWindowRatio;
        label.set({
          width: targetW,
          originX: 'center',
          textAlign: 'center',
          left: frameRect.left! + frameW / 2,
          top: frameRect.top! + frameRect.height! / 2,
        });
        label.initDimensions();
        label.setCoords();
    }

    updateBackgroundImage(url: string | null): void {
        if (!this.editorCanvas) { return; }

        if (url && url === this.backgroundImageUrl && this.backgroundImage) {
            this.applyBackgroundScaling(this.backgroundImage);
            this.editorCanvas.backgroundImage = this.backgroundImage;
            this.editorCanvas.requestRenderAll();
            return;
        }

        // Remove old image
        if (this.backgroundImage) {
            this.editorCanvas.remove(this.backgroundImage);
            this.backgroundImage = null;
        }
        this.backgroundImageUrl = url;

        if (!url) {                         // clear
            this.editorCanvas.backgroundImage = undefined;
            this.editorCanvas.requestRenderAll();
            return;
        }

        fabric.Image.fromURL(url, { crossOrigin: 'anonymous' })
            .then(img => {
                this.backgroundImage = img;
                this.applyBackgroundScaling(img);
                this.editorCanvas.backgroundImage = img;
                this.editorCanvas.requestRenderAll();
                this.logger.debug('[BG] Background image loaded & scaled.');
            })
            .catch(err => {
                this.logger.error('[BG] Failed to load:', err);
                this.notificationService.error('Failed to load background image.').subscribe();
                this.backgroundImageUrl = null;
                this.editorCanvas.requestRenderAll();
            });
    }

    async uploadImage(event: Event, frameIndex: number): Promise<void> {
        if (this.destroyed) {
            return;
        }
        const input = event.target as HTMLInputElement;
        if (!input.files || input.files.length === 0) return;
        const file = input.files[0];
        if (!this.editorCanvas || !this.isCanvasReady) {
            this.notificationService.error('The preview is still loading. Please try again in a moment.');
            input.value = '';
            return;
        }
        if (this.isProcessingSubmit) {
            this.logger.warn('Upload attempted while submit is in progress.');
            input.value = '';
            return;
        }
        const frameState = this.frameStates.find(fs => fs.frameIndex === frameIndex);
        if (!frameState) {
            this.logger.error(`Frame state not found for index: ${frameIndex}`);
            input.value = '';
            return;
        }

        if (frameState.isLoading) {
            this.logger.debug(`Upload already in progress for frame ${frameIndex}`);
            input.value = '';
            return;
        }
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
            this.notificationService.error('You appear to be offline. Please connect to the internet before uploading photos.').subscribe();
            input.value = '';
            return;
        }
        frameState.isLoading = true;
        this.isImageProcessing = true;
        this.validationWarnings[frameIndex] = false;
        this.cd.markForCheck();

        const cleanupOnError = (message: string) => {
            if (message) {
                this.logger.error(message);
                this.notificationService.error(message).subscribe();
            }
            if (frameState.image) {
                this.editorCanvas.remove(frameState.image);
                frameState.image = null;
            }
            frameState.originalImageDataUrl = undefined;
            frameState.thumbnailUploadUrl = undefined;
            frameState.customAsset = null;
            frameState.fileName = null;
            frameState.isLoading = false;
            this.isImageProcessing = false;

            input.value = '';
            this.editorCanvas.requestRenderAll();
            this.cd.markForCheck();
        };
        // 0. convert HEIC if needed(bypass for now)
        let workingFile: File = file;
        try {
            // const isHeic = this.isHeicFile(workingFile);
            // if (isHeic) {
            //     // HEIC path: convert first, then size-check on the JPEG
            //     const jpegFile = await this.convertHeicToJpeg(workingFile);
            //     if (!this.isValidFileSize(jpegFile)) {
            //         const mb = (jpegFile.size / (1024 * 1024)).toFixed(1);
            //         cleanupOnError(`This photo is ${mb}MB after conversion. The maximum allowed size is ${this.maxImageSizeMB}MB.`);
            //         return;
            //     }
            //     workingFile = jpegFile;
            // } else {
                // Normal jpg/png path
                if (!this.isValidFileType(file)) {
                    cleanupOnError('Please upload a JPG, PNG, or HEIC image file.');
                    return;
                }
                if (!this.isValidFileSize(file)) {
                    const mb = (file.size / (1024 * 1024)).toFixed(1);
                    cleanupOnError(`This file is ${mb}MB. The maximum allowed size is ${this.maxImageSizeMB}MB.`);
                    return;
                }
            // }
        } catch (err) {
            cleanupOnError('We could not process this HEIC image. Please try a different image.');
            return;
        }
        // 1. Read the file as Data URL for Fabric display
        const reader = new FileReader();
        reader.onload = (e: ProgressEvent<FileReader>) => {
            if(!e.target) {
                cleanupOnError(`Could not read this file for frame ${frameIndex}. Please try again.`);
                return;
            }
            const imgData = e.target.result as string;
            frameState.originalImageDataUrl = imgData; // Store for potential re-use

            fabric.Image.fromURL(imgData,{ crossOrigin: 'anonymous' })
            .then(async (img) => {
                // Remove previous image for this frame, if exists
                if (frameState.image) {
                    this.editorCanvas.remove(frameState.image);
                }
                this.warnIfLowResolution(img);
                this.scaleImageToFrame(img, frameState.clipRect);
                frameState.image = img;
                this.editorCanvas.add(img);
                this.bringTextFramesToFront();
                this.editorCanvas.setActiveObject(img);
                this.editorCanvas.requestRenderAll();
                this.logger.debug(`Image for frame ${frameIndex} displayed locally.`);

                const { blob: previewBlob, dataUrl: previewDataUrl } = await this.createPreviewBlob(img);
                frameState.thumbnailUploadUrl = previewDataUrl;

                // 2. Upload the original file and preview thumbnail
                try {
                    const  urls = (await this.customImageUploadService.getSignedUploadUrls(
                        workingFile.name,
                        this.productVariantId
                    ).toPromise())!;

                    const uploadTasks = [
                        this.customImageUploadService.uploadToS3(urls.imageUploadUrl, workingFile),
                        this.customImageUploadService.uploadToS3(urls.thumbnailUploadUrl, previewBlob),
                    ];

                    forkJoin(uploadTasks).pipe(
                        finalize(() => {
                            input.value = '';
                            frameState.isLoading = false;
                            this.isImageProcessing = false;
                            this.cd.markForCheck();
                        })
                    ).subscribe({
                        next: () => {
                            frameState.fileName = workingFile.name;
                            frameState.customAsset = urls as any;
                            this.validationWarnings[frameIndex] = false;
                        },
                        error: (err) => {
                            cleanupOnError('Upload failed. Please try again.');
                            this.validationWarnings[frameIndex] = true;
                        }
                    });
                } catch (err: unknown) {
                    cleanupOnError('Upload failed. Please try again.');
                    return;
                }
            }).catch(err => {
                const isPreviewError = err?.message && String(err.message).startsWith('PREVIEW_');
                const msg = isPreviewError
                    ? 'We could not generate a preview for this photo. Please try a different file.'
                    : 'Failed to process the selected file. Please choose another file.';
                cleanupOnError(msg);
            });
        };
        reader.onerror = (error) => {
            cleanupOnError('Could not read this file. Please try again or use another file.');
        };
        reader.readAsDataURL(workingFile);
    }

    /** Remove the image from a given frame and reset all related state */
    deleteImage(frameIndex: number): void {
        const state = this.frameStates.find(fs => fs.frameIndex === frameIndex);
        if (!state) {
            this.logger.error(`deleteImage: frame ${frameIndex} not found`);
            return;
        }

        // 1. Drop the Fabric object from the canvas
        if (state.image) {
            this.editorCanvas.remove(state.image);
            state.image = null;
        }

        // 2. Clear upload-related info
        state.thumbnailUploadUrl = null;
        state.originalImageDataUrl = undefined;
        state.customAsset = null;
        state.fileName = null;
        state.isLoading = false;

        // 3. Mark this frame as needing an upload again
        this.validationWarnings[frameIndex] = true;

        // 4. Re-render & trigger change detection
        this.editorCanvas.requestRenderAll();
        this.cd.markForCheck();

        this.logger.debug(`Image for frame ${frameIndex} deleted`);
    }

    toggleNotesSection(): void {
        this.notesExpanded = !this.notesExpanded;
        this.cd.markForCheck();
        if (this.notesExpanded && this.isMobile) {
            setTimeout(() => {
                const notesElement = document.querySelector('.notes-section');
                if (notesElement) {
                    notesElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 300);
        }
    }

    /**
     * Checks if all required frames have uploaded images and required predefined options are selected.
     */
    canSubmit(): boolean {
        const imagesReady = this.frameStates.every(fs => fs.customAsset && fs.fileName && fs.originalImageDataUrl && !fs.isLoading);
        const paidReady = !this.paidOptions     ||
            this.paidOptions.every((o,i) => !o.required || !!this.paidSelections[i]);
        const messagesReady = !this.messageOptions ||
            this.messageOptions.every((opt, i) => !opt.required || !!this.messageInputs[i]?.trim());
        const textFramesReady = this.textGroupStates.every(gs =>
            gs.frameStates.every(fs => fs.isValid));
        return !this.destroyed && !this.isProcessingSubmit && !this.isUploadingBackground &&
            !this.isFontLoading && imagesReady && !!messagesReady && !!paidReady && textFramesReady;
    }

    /**
     * Build the `text-frame` CustomizableAssetInput entries for the given overlay
     * groups, in group/frame order. Nested overlays stamp `imageFrameName` (so
     * downstream consumers can disambiguate same-named groups across image
     * frames); standalone (canvas-bound) overlays omit it. Optional empty frames
     * are skipped (required-but-empty frames are already blocked by submit()).
     * Shared by submit() so nested overlays persist with the print template and
     * standalone overlays persist inline at their configured option position —
     * matching the modal layout.
     */
    private buildTextFrameAssets(groups: TextFrameGroupState[]): CustomizableAssetInput[] {
        const assets: CustomizableAssetInput[] = [];
        for (const gs of groups) {
            for (const fs of gs.frameStates) {
                if (!fs.content.trim() && !fs.frameData.required) continue;
                const tb = fs.textbox;
                const submittedText = fs.displayText || fs.content;
                const config: TextFrameAssetConfig = {
                    groupName: gs.config.name,
                    position: { x: (tb?.left ?? 0) * this.canvasScale, y: (tb?.top ?? 0) * this.canvasScale },
                    dimension: { width: tb?.width ?? 0, height: tb?.height ?? 0 },
                    scale: { scaleX: (tb?.scaleX ?? 1) * this.canvasScale, scaleY: (tb?.scaleY ?? 1) * this.canvasScale },
                    angleOfRotation: tb?.angle ?? 0,
                    content: submittedText,
                    fontFamily: gs.selectedFont,
                    color: gs.selectedColor,
                    colorName: gs.selectedColorName,
                    textAlign: fs.frameData.textAlign,
                };
                if (gs.parentImageFrameName != null) {
                    config.imageFrameName = gs.parentImageFrameName;
                }
                assets.push({
                    type: 'text-frame',
                    name: fs.frameData.name,
                    text: submittedText,
                    config,
                    productCustomizationOptionId: gs.sourceOptionId || '',
                });
            }
        }
        return assets;
    }

    async submit(): Promise<void> {
        if (!this.canSubmit()) {
            this.logger.warn('Submit called but conditions not met.');
            return;
        }
        // check if all required messages are inputted
        const missingMsgs = this.messageOptions
            .map((opt,i)=>({opt,i}))
            .filter(({opt,i})=> opt.required && !this.messageInputs[i]?.trim());

        if (missingMsgs.length) {
            missingMsgs.forEach(({i}) => this.messageValidation[i] = true);
            this.cd.markForCheck();
            const first = missingMsgs[0].i;
            document.querySelectorAll('.msg-input')[first]?.scrollIntoView({behavior:'smooth',block:'center'});
            return;
        }
        // check if all required paid options are inputted
        const missingPaid = this.paidOptions
            .map((opt,i) => ({opt,i}))
            .filter(({opt,i}) => opt.required && !this.paidSelections[i]);

        if (missingPaid.length) {
            missingPaid.forEach(({i}) => (this.paidValidation[i] = true));
            this.cd.markForCheck();
            const firstIdx = missingPaid[0].i;
            document.querySelectorAll('select.paid-select')[firstIdx]
                ?.scrollIntoView({behavior:'smooth', block:'center'});
            return;
        }
        // Check if all required frames have images
        const missingImages = this.frameStates.filter(fs => !fs.originalImageDataUrl || !fs.customAsset || !fs.fileName);

        if (missingImages.length > 0) {
            missingImages.forEach(fs => {
                this.validationWarnings[fs.frameIndex] = true;
            });

            this.logger.warn(`Missing images for ${missingImages.length} frames.`);
            this.cd.markForCheck();

            if (missingImages.length > 0) {
                const firstMissingIndex = missingImages[0].frameIndex;
                const el = document.getElementById(`imageUploadModal${firstMissingIndex}`);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
                this.missingImagesCount = missingImages.length;
            }
            return;
        }
        // Check required text frames
        const missingTextFrames: { groupIndex: number; frameIndex: number }[] = [];
        for (const gs of this.textGroupStates) {
            for (const fs of gs.frameStates) {
                if (fs.frameData.required && !fs.content.trim()) {
                    fs.isValid = false;
                    missingTextFrames.push({ groupIndex: gs.groupIndex, frameIndex: fs.frameIndex });
                }
            }
        }
        if (missingTextFrames.length) {
            this.cd.markForCheck();
            const first = missingTextFrames[0];
            const el = document.getElementById(`textFrame_${first.groupIndex}_${first.frameIndex}`);
            el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }
        this.isProcessingSubmit = true;
        this.cd.markForCheck();
        this.logger.debug('Preparing submission data...');

        // Deactivate any active object for clean final state capture
        this.editorCanvas.discardActiveObject();
        this.editorCanvas.renderAll();

        // --- Create the Output Data ---
        const outputData: CustomizableAssetInput[] = [];

        this.uploadMergedCanvasFailed = false;
        // upload merged image and preview of merged image
        const mergedCanvas = this.editorCanvas.toDataURL({
            multiplier: 1.0,
            format: 'png',
            quality: 0.9
        });
        const mergedBlob: Blob = await (await fetch(mergedCanvas)).blob();

        // compute scale so max(width,height) becomes 100px
        const maxPreviewSize = 100;
        const canvasW = this.editorCanvas.getWidth();
        const canvasH = this.editorCanvas.getHeight();
        const previewScale = Math.min(maxPreviewSize / canvasW, maxPreviewSize / canvasH);
        const previewCanvas = this.editorCanvas.toDataURL({
            multiplier: previewScale,
            format: 'png',
            quality: 0.9
        });
        const previewBlob: Blob = await (await fetch(previewCanvas)).blob();

        try {
            // convert the entire pipe into a single promise
            const urls = (await this.customImageUploadService
              .getSignedUploadUrls(
                'preview.png',
                this.productVariantId,
              )
              .toPromise())!;

            await Promise.all([
                this.customImageUploadService.uploadToS3(urls.imageUploadUrl, mergedBlob).toPromise(),
                this.customImageUploadService.uploadToS3(urls.thumbnailUploadUrl, previewBlob).toPromise(),
            ]);

            // both uploads are done
            const previewConfig = {
                dimension: {
                    width: this.printingPattern!.canvasWidth,
                    height: this.printingPattern!.canvasHeight,
                }
            };
            outputData.push({
              type: 'preview-container',
              config: previewConfig,
              name: 'preview',
              text: `Preview Image`,
              imageKey: urls.imageKey,
              thumbnailImageKey: urls.thumbnailImageKey,
              productCustomizationOptionId: this.printingPattern?.optionId || '',
            });
            this.cd.markForCheck();
        } catch (err) {
            this.notificationService.error('Upload failed. Please try again.').subscribe();
            this.uploadMergedCanvasFailed = true;
            this.isProcessingSubmit = false;
            this.logger.error('Upload pipeline failed', err);
            return;
        }

        // add each frame image
        const frameAssets = this.frameStates?.map(state => {
            const originalFrameData = this.printingPattern?.imageFrames[state.frameIndex];
            if(!originalFrameData) return null;
            let frameAsset: any = null;
            if (state.image) {
                const imageConfig = {
                    position: {
                        x: state.image.left * this.canvasScale,
                        y: state.image.top * this.canvasScale,
                    },
                    dimension: {
                        width: state.image.width,
                        height: state.image.height,
                    },
                    scale: {
                        scaleX: state.image.scaleX * this.canvasScale,
                        scaleY: state.image.scaleY * this.canvasScale,
                    },
                    angleOfRotation: state.image.angle,
                };
                frameAsset = {
                    type: 'image',
                    config: imageConfig,
                    name: state.fileName,
                    text: originalFrameData.name ? originalFrameData.name : `Uploaded Photo ${state.frameIndex+1}`,
                    imageKey: state.customAsset?.imageKey || null,
                    thumbnailImageKey: state.customAsset?.thumbnailImageKey || null,
                    productCustomizationOptionId: this.printingPattern?.optionId || '',
                };
            }
            return frameAsset;
        }).filter(notNullOrUndefined);
        outputData.push(...frameAssets);
        // Nested overlays belong to the print template's image frames — persist
        // them right after the frame images so their order matches the modal
        // (overlays render under their owning uploader, not in a trailing block).
        outputData.push(...this.buildTextFrameAssets(
            this.textGroupStates.filter(gs => gs.parentImageFrameName != null),
        ));
        // add background image
        const chosenBackground = this.printingPattern?.backgroundImages[this.selectedBackgroundIndex];
        if(chosenBackground?.asset?.imageUrl || chosenBackground?.asset?.preview) {
            outputData.push({
                type: 'text',
                name: 'background-image',
                text: 'Background Image',
                config: {
                    label: 'Background Image',
                    content: chosenBackground?.label,
                },
                productCustomizationOptionId: this.printingPattern?.optionId || '',
            });
        }
        // Add message + paid options in custom-option settings order (variant
        // first). `orderedOptions` mirrors that order, so we persist assets in the
        // same sequence the modal renders them rather than in type-blocks.
        for (const entry of this.orderedOptions) {
            if (entry.kind === 'message') {
                const opt = this.messageOptions[entry.index];
                const content = this.messageInputs[entry.index]?.trim();
                if (!opt || !content) { continue; }
                outputData.push({
                    type: 'text',
                    name: 'customization-notes',
                    text: opt.label,
                    config: {
                        label: opt.label,
                        content,
                        required: opt.required,
                    },
                    productCustomizationOptionId: opt.optionId || '',
                });
            } else if (entry.kind === 'paid') {
                const opt = this.paidOptions[entry.index];
                const chosen = this.paidSelections[entry.index];
                if (!opt || !chosen) { continue; }
                outputData.push({
                    type: 'paid-option',
                    name: opt.label,
                    text: chosen.label,
                    config: {
                        label: opt.label,
                        content: chosen.label,
                        key: chosen.key,
                        required: opt.required,
                    },
                    amount: chosen.price,
                    productCustomizationOptionId: opt.optionId || '',
                });
            } else if (entry.kind === 'textframe') {
                // Standalone (canvas-bound) custom-text-frames option — persist
                // its overlay groups inline at the configured position, matching
                // the modal render order.
                outputData.push(...this.buildTextFrameAssets(
                    this.textGroupStates.filter(
                        gs => !gs.parentImageFrameName &&
                            (gs.sourceOptionId || '') === (entry.optionId || ''),
                    ),
                ));
            }
        }
        // Any standalone overlay whose option id wasn't represented in
        // orderedOptions (legacy no-customizationOptions path) — emit last so
        // nothing is silently dropped. Nested overlays were already emitted
        // with the print template's frame images above.
        const orderedTextFrameOptionIds = new Set(
            this.orderedOptions
                .filter(o => o.kind === 'textframe')
                .map(o => o.optionId || ''),
        );
        outputData.push(...this.buildTextFrameAssets(
            this.textGroupStates.filter(
                gs => !gs.parentImageFrameName &&
                    !orderedTextFrameOptionIds.has(gs.sourceOptionId || ''),
            ),
        ));

        this.logger.debug('Output data prepared:', outputData);
        this.resolveWith(outputData);
    }

    cancel(): void {
        this.logger.debug('Modal cancelled by user.');
        this.resolveWith(undefined);
    }
}